import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    getUnitListPrice,
    getUnitNetPrice,
    getUnitRegularPriceWithVat,
} from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CallForPrice from "@tKgpco/Components/CallForPrice";
import { TYPOGRAPHY } from "@tKgpco/theme/design";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    label?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
    onClick?: React.EventHandler<React.MouseEvent>;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    currentLocation: getLocation(state),
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    isSalesPerson: state.context.session.isSalesPerson,
    isAdmin: state.context?.session?.userRoles?.toLowerCase()?.includes("administrator"),
    canshowPrice: state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    requesitionPrice?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;
    secondaryPriceWrapper?: InjectableCss;
    secondaryPrice?: PriceStyles;
    unitOfMeasure?: UnitOfMeasureStyles;
    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    loginAction?: LinkPresentationProps;
    indicator?: InjectableCss;
    itemNotFound?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
    vatLabelText?: TypographyPresentationProps;
}
interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
        `,
    },
    priceLabelText: {
        variant: "p",
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 0;
        `,
    },
    requesitionPrice: {
        variant: "p",
        size: TYPOGRAPHY.size.small,
        transform: "uppercase",
        weight: 600,
        css: css`
            @media print {
                font-size: 12px;
            }
            margin-bottom: 0;
        `,
    },
    priceWrapper: {
        css: css`
            ${wrapInContainerStyles};
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        `,
    },
    price: {
        priceText: {
            weight: 600,
        },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },

        unitOfMeasureText: {
            css: css`
                white-space: nowrap;
            `,
        },

        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    secondaryPriceWrapper: {
        css: css`
            margin-top: 5px;
            ${wrapInContainerStyles}
        `,
    },
    secondaryPrice: {
        priceText: { weight: "bold" },
        errorText: { weight: "bold" },
        realTimeText: {
            weight: "bold",
            css: css`
                display: inline-block;
                text-align: left;
                min-width: 25px;

                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },

        vatLabelText: {
            css: css`
                font-size: 12px;
            `,
        },
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    packLabelText: {
        weight: "bold",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },
    // quoteMessage: {
    //     text: { weight: "bold" },
    // },
    quoteMessage: {
        text: { weight: "bold" },
        tooltip: {
            typographyProps: {
                css: css`
                    @media (max-width: 1127px) and (min-width: 990px) {
                        width: 122px;
                    }
                `,
            },
        },
    },
    quoteMessageWrapper: {
        css: css`
            width: max-content;
            display: flex;
            // border: 1px solid #e6b23d;
            // border-radius: 4px;
            font-size: ${TYPOGRAPHY.size.smaller};
            color: #f0b003;
            // padding: 2px 5px;
            margin: 15px 0;
            div[class^="TooltipWrapper"] {
                button {
                    span {
                        color: #f0b003;
                        height: 15px;
                    }
                }
            }
        `,
    },
    indicator: {
        css: css`
            font-size: 14px;
            font-color: orange;
        `,
    },
    itemNotFound: {
        css: css`
            font-size: 14px;
            margin: 12px 0px;
            span {
                padding-right: 5px;
            }
        `,
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    label,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,
    enableVat,
    vatPriceDisplay,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
    currentLocation,
    onClick,
    showSalePriceLabel = true,
    signInUrl,
    isSalesPerson,
    isAdmin,
    canshowPrice,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));

    if (!showPricing && !isCheckoutReviewAndSubmitPage) {
        return null;
    }

    const productContextModel = pricingData as ProductContextModel;
    const cartLineModel = pricingData as CartLineModel;

    const additionalResults = productContextModel.productInfo?.pricing?.additionalResults;

    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure !== pricingData.baseUnitOfMeasure &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};
    const secondaryPriceStyles = styles.secondaryPrice || {};
    const returnUrl = currentLocation.pathname + currentLocation.search;
    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                    <Link
                        {...styles.loginAction}
                        href={`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}`}
                        alt="login"
                        onClick={onClick}
                        data-test-selector="loginLink"
                    >
                        {siteMessage("Pricing_SignInForPrice")}
                    </Link>
                </Typography>
            </StyledWrapper>
        );
    }

    if (!canshowPrice) {
        return (
            <UnitOfMeasure
                unitOfMeasure={unitOfMeasure}
                showUnitOfMeasure={showUnitOfMeasure}
                styles={styles.unitOfMeasure}
                onlyUOM={true}
            />
        );
    }

    if ("product" in pricingData && pricingData.productInfo.failedToLoadPricing) {
        return (
            <SectionWrapper {...styles.wrapper}>
                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("RealTimePricing_PriceLoadFailed")}
                </Typography>
            </SectionWrapper>
        );
    }

    if (quoteRequired) {
        return (
            <SectionWrapper {...styles.wrapper}>
                {showLabel && <Typography {...styles.priceLabelText}>{translate("Price")}</Typography>}
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Submit for Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                    />
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    const displayVatForPrice = vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat";
    const showSecondaryPrice = enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat";
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel || cartLineModel?.salePriceLabel || translate("Sale Price:")
        : "";

    return (
        <SectionWrapper {...styles.wrapper}>
            {showLabel && <Typography {...styles.requesitionPrice}>{label || translate("Price")}</Typography>}
            <StyledWrapper {...styles.priceWrapper} data-test-selector="priceIncludeVat">
                <Price
                    currencySymbol={currencySymbol}
                    pricing={pricing}
                    qtyOrdered={qtyOrdered}
                    unitOfMeasure={unitOfMeasure}
                    showUnitOfMeasure={showUnitOfMeasure}
                    enableVat={enableVat}
                    displayWithVat={displayVatForPrice}
                    styles={priceStyles}
                    salePriceLabel={salePriceLabel}
                    additionalResults={additionalResults}
                    isSalesPerson={isSalesPerson}
                    isAdmin={isAdmin}
                    productId={cartLineModel.productId}
                />
                {isInvalidPrice && showInvalidPriceMessage && (
                    <Typography as="p" {...styles.invalidPriceText} data-test-selector="productPrice_invalid">
                        {translate("Invalid Price")}
                    </Typography>
                )}
            </StyledWrapper>
            {showSecondaryPrice && (
                <StyledWrapper {...styles.secondaryPriceWrapper} data-test-selector="priceExcludeVat">
                    <Price
                        currencySymbol={currencySymbol}
                        pricing={pricing}
                        qtyOrdered={qtyOrdered}
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        enableVat={enableVat}
                        displayWithVat={false}
                        styles={secondaryPriceStyles}
                    />
                </StyledWrapper>
            )}
            {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                    {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                </Typography>
            )}
            {packDescription && showPack && (
                <StyledWrapper {...styles.packWrapper}>
                    <Typography {...styles.packLabelText}>{translate("Pack")}:&nbsp;</Typography>
                    <Typography {...styles.packDescriptionText}>{packDescription}</Typography>
                </StyledWrapper>
            )}
            {pricing && showSavings && (
                <ProductPriceSavingsMessage
                    pricing={pricing}
                    qtyOrdered={qtyOrdered || 1}
                    showSavingsAmount={showSavingsAmount}
                    showSavingsPercent={showSavingsPercent}
                    currencySymbol={currencySymbol}
                    {...styles.savingsMessageText}
                />
            )}
        </SectionWrapper>
    );
};

interface PriceProps {
    currencySymbol: string;
    pricing: ProductPriceDto | null | undefined;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    enableVat: boolean;
    displayWithVat: boolean;
    styles: PriceStyles;
    salePriceLabel?: string;
    additionalResults?: any;
    isSalesPerson?: boolean;
    isAdmin?: boolean;
    productId?: string | null;
}

const Price = ({
    currencySymbol,
    pricing,
    qtyOrdered,
    unitOfMeasure,
    showUnitOfMeasure = true,
    enableVat,
    displayWithVat,
    styles,
    salePriceLabel,
    additionalResults,
    isSalesPerson,
    isAdmin,
    productId,
}: PriceProps) => {
    let salePriceLabelPrefix = "";
    if (pricing && salePriceLabel) {
        const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered || 1);
        const { price: unitListPrice } = getUnitListPrice(pricing, qtyOrdered || 1);
        if (unitNetPrice < unitListPrice) {
            salePriceLabelPrefix = `${salePriceLabel} `;
        }
    }
    let indicator = "";
    if (additionalResults?.showPriceMessage && additionalResults?.showPriceMessage?.toLowerCase() === "true") {
        if (additionalResults?.isDealerPrice && additionalResults?.isDealerPrice?.toLowerCase() === "true") {
            indicator = translate("DLR");
        } else if (
            additionalResults?.salesPriceSource &&
            additionalResults?.salesPriceSource?.toLowerCase() === "sales price"
        ) {
            indicator = translate("TA");
        } else if (
            additionalResults?.salesPriceSource &&
            additionalResults?.salesPriceSource?.toLowerCase() === "screen price"
        ) {
            indicator = translate("LST");
        } else {
            indicator = translate("SP");
        }
    } else if (productId) {
        if (window.localStorage.getItem("customInventory")) {
            const inventory = JSON.parse(window.localStorage.getItem("customInventory") || "null");
            if (inventory && inventory.length > 0) {
                const filterInventory = inventory.filter((data: any) => data.productId === productId);
                if (filterInventory.length > 0) {
                    if (
                        filterInventory[0].additionalResults?.showPriceMessage &&
                        filterInventory[0].additionalResults?.showPriceMessage?.toLowerCase() === "true"
                    ) {
                        if (
                            filterInventory[0].additionalResults?.isDealerPrice &&
                            filterInventory[0].additionalResults?.isDealerPrice?.toLowerCase() === "true"
                        ) {
                            indicator = translate("DLR");
                        } else if (
                            filterInventory[0].additionalResults?.salesPriceSource &&
                            filterInventory[0].additionalResults?.salesPriceSource?.toLowerCase() === "sales price"
                        ) {
                            indicator = translate("TA");
                        } else if (
                            filterInventory[0].additionalResults?.salesPriceSource &&
                            filterInventory[0].additionalResults?.salesPriceSource?.toLowerCase() === "screen price"
                        ) {
                            indicator = translate("LST");
                        } else {
                            indicator = translate("SP");
                        }
                    }
                }
            }
        }
    }

    const indicatorStyle: InjectableCss = {
        css: css`
            color: #e6b23d;
            padding-left: 4px;
            font-weight: 600;
        `,
    };
    return (
        <>
            {pricing?.unitRegularPrice === 0 ? (
                <CallForPrice />
            ) : pricing ? (
                <Typography {...styles.priceText} data-test-selector="productPrice_unitNetPrice">
                    {salePriceLabelPrefix}
                    {enableVat && displayWithVat
                        ? getUnitRegularPriceWithVat(pricing, qtyOrdered || 1).priceDisplay
                        : getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                </Typography>
            ) : (
                <Typography {...styles.realTimeText} data-test-selector="productPrice_unitNetPrice">
                    {currencySymbol}
                </Typography>
            )}
            {pricing?.unitRegularPrice === 0
                ? null
                : unitOfMeasure &&
                  showUnitOfMeasure && (
                      <UnitOfMeasure
                          unitOfMeasure={unitOfMeasure}
                          showUnitOfMeasure={showUnitOfMeasure}
                          styles={styles.unitOfMeasure}
                      />
                  )}
            {(isSalesPerson || isAdmin) && indicator && pricing?.unitRegularPrice !== 0 && (
                <StyledWrapper {...indicatorStyle}>{indicator}</StyledWrapper>
            )}
            {pricing && enableVat && (
                <Typography as="p" {...styles.vatLabelText}>
                    {displayWithVat ? `${translate("Inc. VAT")} (${pricing.vatRate}%)` : translate("Ex. VAT")}
                </Typography>
            )}
        </>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
    onlyUOM?: boolean;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles, onlyUOM = false }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            {onlyUOM ? "UOM:" : <>&nbsp;/&nbsp;</>} {unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
